import { API_BASE_URL } from "../constants.js";
import type { ChatUiAnswer } from "./chatUiActionHelpers";

const ANSWER_TIMEOUT_MS = 8_000;
const LOCAL_API_FALLBACK_BASE = "http://127.0.0.1:8080";
const ANSWER_RETRY_MS = 200;
const ANSWER_RETRIES = 6;

export class UiActionAnswerError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "UiActionAnswerError";
    this.status = status;
  }
}

function makeAnswerUrl(actionId: string, baseUrl: string): string {
  return `${baseUrl}/api/harper/ui-actions/${encodeURIComponent(actionId)}/answer`;
}

function canUseLocalAnswerFallback(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.protocol === "file:" || !!(window as any).electron;
}

async function fetchAnswerWithTimeout(
  url: string,
  init: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), ANSWER_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function fetchAnswerWithRetry(
  url: string,
  init: RequestInit,
): Promise<Response> {
  let response: Response | null = null;
  for (let attempt = 0; attempt < ANSWER_RETRIES; attempt += 1) {
    response = await fetchAnswerWithTimeout(url, init);
    if (response.ok || response.status !== 404) return response;
    await sleep(ANSWER_RETRY_MS);
  }
  return response ?? fetchAnswerWithTimeout(url, init);
}

export async function postUiActionAnswer(
  actionId: string,
  status: "answered" | "cancelled" | "approved" | "denied",
  answers: ChatUiAnswer[] = [],
  feedback?: string,
) {
  const electronAnswer = (window as any).electron?.answerUiAction;
  if (typeof electronAnswer === "function") {
    const result = await electronAnswer({
      actionId,
      status,
      answers,
      feedback,
    });
    if (result?.ok) return;
    throw new Error(result?.error ?? "Approval answer failed");
  }

  const init: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, answers, feedback }),
  };
  const localRuntime = canUseLocalAnswerFallback();
  const primaryBase = localRuntime ? LOCAL_API_FALLBACK_BASE : API_BASE_URL;
  try {
    const response = await fetchAnswerWithRetry(
      makeAnswerUrl(actionId, primaryBase),
      init,
    );
    if (!response.ok)
      throw new UiActionAnswerError(
        `Approval answer failed: ${response.status}`,
        response.status,
      );
    return;
  } catch (error) {
    const canRetry = localRuntime && primaryBase !== API_BASE_URL;
    if (!canRetry) throw error;
    const response = await fetchAnswerWithRetry(
      makeAnswerUrl(actionId, API_BASE_URL),
      init,
    );
    if (!response.ok)
      throw new UiActionAnswerError(
        `Approval answer failed: ${response.status}`,
        response.status,
      );
  }
}
